import { Controller, type FieldValues } from 'react-hook-form';
import { TimePicker, type TimePickerProps } from 'antd';
import { formDayJsValue } from 'pos-client/utils/dayjs.ts';

import {
  extractPosFormItemProps,
  type PosFormComponentType,
} from './FormUtils.ts';
import { PosFormItem } from './PosFormItem.tsx';

export type PosTimePickerProps = Omit<
  TimePickerProps,
  'onChange' | 'value' | 'name'
>;

export type PosTimePickerPropsType<
  TFieldValues extends FieldValues = FieldValues,
> = PosFormComponentType<TimePickerProps, TFieldValues>;

/**
 * @component
 * @description Time picker with React-Hook-Form integration
 * @see {@link https://ant.design/components/date-picker#date-picker-demo-time | AntD Time Picker Component}
 *
 * @param PosTimePickerPropsType.control Control field from React-Hook-Form useForm
 * @remarks If an invalid control from useForm is passed then the Input will behave as an uncontrolled component and field name will not be type-safe
 * @param PosTimePickerPropsType.name Name of form field
 * @example
 * ```tsx
 * // Controlled (RHF) usage:
 * const { t } = useTranslation();
 * const { control } = useForm<TradingForm>();
 *
 * const additionalPickerProps: Partial<PosTimePickerProps> = {
 *   use12Hours: true,
 *   showNow: false,
 *   allowClear: false,
 * };
 *
 * <PosTimePicker
 *   control={control}
 *   name="startingTime"
 *   label={t('locations.trading.startingTime')}
 *   {...additionalPickerProps}
 * />
 *
 * // Uncontrolled (No RHF) usage:
 * const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(null);
 *
 * <PosTimePicker
 *   onChange={setStartTime}
 *   value={startTime}
 *   {...additionalPickerProps}
 * />
 * ```
 */
export const PosTimePicker = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  required,
  minuteStep = 15,
  format = 'HH:mm',
  ...props
}: PosTimePickerPropsType<TFieldValues>) => {
  const isControlled = !!control;

  const { posFormItemProps, rest: timePickerProps } =
    extractPosFormItemProps(props);

  const onTimeChange = (
    time: unknown,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultOnChange: (...event: any[]) => void
  ) => defaultOnChange(formDayJsValue(time) ?? null);

  if (isControlled)
    return (
      <PosFormItem
        control={control}
        name={name}
        required={required}
        {...posFormItemProps}
      >
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value, ...field } }) => (
            <TimePicker
              {...timePickerProps}
              {...field}
              format={format}
              onChange={(date) => onTimeChange(date, onChange)}
              value={formDayJsValue(value)}
              data-testid={name}
              required={required}
              aria-required={required}
            />
          )}
        />
      </PosFormItem>
    );

  return (
    <TimePicker
      name={name}
      format={format}
      minuteStep={minuteStep}
      required={required}
      {...props}
    />
  );
};
